import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import type { DateTime } from 'luxon';
import type { DurchfuehrungsTag, Product } from '../_cms/products/entities/Product.js';
import { ProductKGF } from '../_cms/products/entities/ProductKGF.js';
import { ProductLKK } from '../_cms/products/entities/ProductLKK.js';
import { ProductOGT } from '../_cms/products/entities/ProductOGT.js';
import type { KPF } from '../master-data-management/kpf/entities/KPF.js';
import { archiveCourse, Course } from './Course.js';

export enum CourseLernfoerderungVersion {
  V1 = 'V1',
  V2 = 'V2',
}

type SupportedProduct = ProductLKK | ProductOGT | ProductKGF;

/**
 * @entity CourseLernfoerderung
 */
@Entity()
export class CourseLernfoerderung extends Course {
  public static override _class = 'CourseLernfoerderung';

  @Field(() => String)
  version: Maybe<CourseLernfoerderungVersion>;

  static override async beforeCreate(course: CourseLernfoerderung) {
    await CourseLernfoerderung.checkForDuplicateCourses(course);
    await super.beforeCreate(course);
  }

  private static async checkForDuplicateCourses(course: CourseLernfoerderung) {
    if (!course.kpfID || !course.weekday || !course.location || course.startTime || course.endTime) {
      return;
    }
    const similarCourses = await loadMongoData(CourseLernfoerderung, {
      _class: CourseLernfoerderung._class,
      kpfID: course.kpfID,
      weekday: course.weekday,
      location: course.location,
    });

    if (
      similarCourses.some(
        c =>
          c.startTime &&
          c.endTime &&
          course.startTime!.hasSame(c.startTime, 'hour') &&
          course.startTime!.hasSame(c.startTime, 'minute') &&
          course.endTime!.hasSame(c.endTime, 'hour') &&
          course.endTime!.hasSame(c.endTime, 'minute'),
      )
    ) {
      throw new Error('A course with the same location and weekday already exists at this time');
    }
  }
}

/**
 * Single source of truth for which LKK course generation a view shows. Planning, overview and
 * attendance used to decide this differently, so a KPF could show courses in attendance that the
 * planning hides (and therefore nobody can delete).
 *
 * - `useV2Courses` off: only V1 (or unversioned) courses, `archiveV1After` is ignored.
 * - `useV2Courses` on: only V2 courses. Attendance passes a `day` and additionally keeps the V1 courses
 *   accessible up to `archiveV1After` (or forever without that date), which is what V1 history relies on.
 *
 * Anything that is not an LKK Lernförderung course is not affected.
 */
export function isCourseOfActiveGeneration(course: Course, produkt: Maybe<Product>, day?: DateTime): boolean {
  if (!(course instanceof CourseLernfoerderung) || !(produkt instanceof ProductLKK)) return true;
  const isV2 = course.version === CourseLernfoerderungVersion.V2;
  if (!produkt.useV2Courses) return !isV2;
  if (!day) return isV2;
  const switchDay = produkt.archiveV1After?.startOf('day');
  return !switchDay || isV2 === day >= switchDay;
}

/**
 * Identity of a course slot: weekday + start + end. Rooms are deliberately not part of it,
 * because one Durchführungstag may be served by several courses (one per room).
 *
 * Returns null when the slot is incomplete. Every caller has to handle that - an incomplete
 * day or course must never be able to throw, because the whole synchronization runs inside a
 * catch-all in KPF.afterUpdate and a throw here silently drops *all* course updates for that KPF.
 */
function keyof(w: Maybe<string>, s: Maybe<DateTime>, e: Maybe<DateTime>): Maybe<string> {
  if (!w || !s || !e) return null;
  return `${w}-${s.toFormat('HH:mm')}-${e.toFormat('HH:mm')}`;
}

const courseKey = (course: Pick<Course, 'weekday' | 'startTime' | 'endTime'>) =>
  keyof(course.weekday, course.startTime, course.endTime);

const tagKey = (tag: DurchfuehrungsTag) => keyof(tag.wochentag, tag.startZeit, tag.endZeit);

/** A course nobody is assigned to can be archived without losing anything. */
const isCourseEmpty = (course: Course) =>
  !course.osStudentIDs?.length && !course.usStudentIDs?.length && !course.leKoIDs?.length && !course.schuKoIDs?.length;

export function courseMatchesDurchfuehrungsTag(course: Course, tag: DurchfuehrungsTag): boolean {
  const key = courseKey(course);
  return !!key && key === tagKey(tag);
}

/**
 * True when the course sits on one of the KPF's Durchführungstage.
 *
 * A course that matches none of them was created or edited with a day/time the product does not
 * offer. It then matches no student's day wish either, which silently makes every student in it look
 * misassigned and drops them out of the user pool - so callers should surface it rather than swallow it.
 *
 * With no Durchführungstage configured there is nothing to check against, so nothing is claimed.
 */
export function courseMatchesAnyDurchfuehrungsTag(course: Course, days: DurchfuehrungsTag[]): boolean {
  if (!days.length) return true;
  return days.some(day => courseMatchesDurchfuehrungsTag(course, day));
}

export function checkDuplicateDurchfuehrungsTage(days: DurchfuehrungsTag[]) {
  const seenKeys = new Set<string>();
  for (const day of days) {
    // incomplete days cannot collide with anything - they are rejected by validateDurchfuehrungsTage
    const key = tagKey(day);
    if (!key) continue;
    if (seenKeys.has(key)) {
      throw new Error(
        `Duplicate DurchfuehrungsTag detected for weekday ${day.wochentag} at ${day.startZeit?.toFormat(
          'HH:mm',
        )} - ${day.endZeit?.toFormat('HH:mm')}`,
      );
    }
    seenKeys.add(key);
  }
}

/**
 * Rejects Durchführungstage that are missing a weekday or a time. Such a day used to be accepted
 * on create and produced a course without weekday and times, which is invisible in the
 * CHANCENkräfte overview and poisoned every later synchronization of that KPF.
 */
export function validateDurchfuehrungsTage(days: DurchfuehrungsTag[]) {
  for (const day of days) {
    if (tagKey(day)) continue;
    throw new ValidationError(
      'Jeder Durchführungstag braucht einen Wochentag, eine Start- und eine Endzeit. ' +
        'Bitte vervollständige die Lernförderung.',
    );
  }
}

export async function synchronizeKPFCoursesWithDB(kpf: KPF<SupportedProduct>, oldKPF?: KPF<SupportedProduct>) {
  if (!kpf.produkt || ![ProductLKK._class, ProductOGT._class, ProductKGF._class].includes(kpf.produkt?._class)) {
    return;
  }

  const all_courses = (
    await loadMongoData(CourseLernfoerderung, { _class: CourseLernfoerderung._class, kpfID: kpf._id })
  ).map(c => constructValue(c) as CourseLernfoerderung);

  const olddays = oldKPF?.produkt?.durchfuehrungsTage ?? [];
  const newdays = kpf.produkt.durchfuehrungsTage ?? [];

  // * 1 - a day that still exists but moved: carry its courses over to the new weekday/time
  for (const oldday of olddays) {
    const newday = newdays.find(d => d.cid === oldday.cid);
    if (!newday) continue; // removed day, handled in step 2

    const oldkey = tagKey(oldday);
    const newkey = tagKey(newday);
    // an incomplete day on either side has no courses to carry over
    if (!oldkey || !newkey || oldkey === newkey) continue;

    for (const course of all_courses.filter(c => !c.archivedAt && courseKey(c) === oldkey)) {
      course.weekday = newday.wochentag;
      course.startTime = newday.startZeit;
      course.endTime = newday.endZeit;
      await course.save();
    }
  }

  // * 2 - a day was removed: archive its courses, but only while nobody is assigned to them.
  // ! Courses with students, LeKos or SchuKos are kept on purpose - dropping them out of the
  // ! CHANCENkräfte overview would silently detach real people. Those have to be resolved by hand.
  for (const oldday of olddays) {
    if (newdays.some(d => d.cid === oldday.cid)) continue;

    const oldkey = tagKey(oldday);
    if (!oldkey) continue;
    // the day may have been removed *and* re-added under a different cid
    if (newdays.some(d => tagKey(d) === oldkey)) continue;

    for (const course of all_courses.filter(c => !c.archivedAt && courseKey(c) === oldkey)) {
      if (!isCourseEmpty(course)) {
        logger.warn(
          `[Lernförderung] Durchführungstag ${oldkey} of KPF ${kpf._id} was removed, but course ${course._id} still has people assigned - keeping it.`,
        );
        continue;
      }
      await archiveCourse(course);
    }
  }

  const v2attr = (() => {
    if (kpf.produkt?._class === ProductLKK._class) {
      if ((kpf.produkt as ProductLKK).useV2Courses) {
        return { version: CourseLernfoerderungVersion.V2 };
      } else {
        return { version: CourseLernfoerderungVersion.V1 };
      }
    }
    return {};
  })();

  // * 3 - every day must be backed by at least one course, otherwise it does not show up in the
  // * CHANCENkräfte overview. This reconciles against the actual courses instead of diffing cids,
  // * so it also heals days that never got a course (e.g. days that predate this synchronization).
  const activeKeys = new Set(
    all_courses
      .filter(c => !c.archivedAt)
      .flatMap(c => {
        const k = courseKey(c);
        return k ? [k] : [];
      }),
  );
  for (const newday of newdays) {
    const key = tagKey(newday);
    if (!key || activeKeys.has(key)) continue;

    // create one single course without a room - rooms are assigned afterwards in the CHANCENboard
    await new CourseLernfoerderung({
      kpfID: kpf._id,
      weekday: newday.wochentag,
      startTime: newday.startZeit,
      endTime: newday.endZeit,
      location: '',
      ...v2attr,
    }).create();
    activeKeys.add(key);
  }
}
